import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Resizer from 'react-image-file-resizer';

import Button from 'components/Button';
import Icon from 'components/Icon';

import { getSizeInMB, getUserPhotoURL } from 'utils';

import { ModalContainer } from './AvatarModal.styled';

const AvatarModal = ({
  toggleAvatarModal,
  onSubmit,
  currentAvatar,
  changeAvatar,
  buttonLabel,
  showImageUploadSizeLimitError,
}) => {
  const fileUploader = useRef();

  const [avatarURL, setAvatarURL] = useState(() => {
    if (currentAvatar.startsWith('avatar-')) {
      return '';
    }

    return getUserPhotoURL(currentAvatar, false);
  });

  const openFileUploader = useCallback(() => {
    if (fileUploader.current) {
      fileUploader.current?.click();
    }
  }, [fileUploader]);

  const onAvatarChange = useCallback(
    (e) => {
      if (e.target.dataset.id === 'image') {
        changeAvatar(avatarURL);
      } else {
        const value = `avatar-${e.target.dataset.id}`;

        changeAvatar(value);
      }
    },
    [changeAvatar, avatarURL],
  );

  const resizeFile = useCallback(
    (file) =>
      new Promise((resolve) => {
        Resizer.imageFileResizer(
          file,
          140,
          140,
          'JPEG',
          50,
          0,
          (uri) => {
            resolve(uri);
          },
          'base64',
        );
      }),
    [],
  );

  const onImageChange = useCallback(
    // eslint-disable-next-line consistent-return
    (e) => {
      const file = e.target.files[0];

      if (file) {
        if (file.size > getSizeInMB(5)) {
          return showImageUploadSizeLimitError();
        }

        return (async () => {
          try {
            const base64 = await resizeFile(file);

            setAvatarURL(base64);

            changeAvatar(base64);
          } catch (err) {
            // do nothing
          }
        })();
      }
    },
    [resizeFile, changeAvatar, showImageUploadSizeLimitError],
  );

  return (
    <ModalContainer className="absolute-inset">
      <div style={{ width: 428 }}>
        <Button type="button" className="close" onClick={toggleAvatarModal}>
          <Icon
            name="close"
            height="22px"
            width="22px"
            verticalAlign="bottom"
          />
        </Button>
        <h3 className="center">Avatars</h3>
        <div className="avatars">
          <Button type="button" data-id="image" onClick={openFileUploader}>
            <div className="fake-image">&#43;</div>
          </Button>
          {avatarURL && (
            <Button
              type="button"
              data-id="image"
              onClick={onAvatarChange}
              className={avatarURL === currentAvatar ? 'selected' : ''}>
              <img src={avatarURL} alt="avatar" height="48px" width="48px" />
              <span>&#10003;</span>
            </Button>
          )}
          {Array.from(Array(30).keys()).map((i) => (
            <Button
              key={i}
              type="button"
              data-id={i}
              onClick={onAvatarChange}
              className={`avatar-${i}` === currentAvatar ? 'selected' : ''}>
              <img
                src={`/assets/img/avatars/avatar-${i}.svg`}
                alt="avatar"
                height="48px"
                width="48px"
              />
              <span>&#10003;</span>
            </Button>
          ))}
        </div>
        <Button type="button" styled fluid onClick={onSubmit}>
          {buttonLabel}
        </Button>
        <input
          ref={fileUploader}
          type="file"
          accept="image/*"
          onChange={onImageChange}
          style={{ display: 'none' }}
        />
      </div>
    </ModalContainer>
  );
};

AvatarModal.propTypes = {
  toggleAvatarModal: PropTypes.func.isRequired,
  changeAvatar: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  currentAvatar: PropTypes.string,
  buttonLabel: PropTypes.string,
  showImageUploadSizeLimitError: PropTypes.func.isRequired,
};

AvatarModal.defaultProps = {
  buttonLabel: 'Select',
};

export default AvatarModal;
